import React, { useEffect, useState } from 'react'
import { Table, Spin, Empty, Button, message, Space, Modal } from 'antd'
import { DownloadOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons'
import { getStudentCertificates } from '../../../../../Constants/Api/functions/function'
import dayjs from 'dayjs'
import {
    DEFAULT_CERTIFICATE_LAYOUT,
    normalizeCertificateLayout,
    formatCertificateText,
    getCertificateFieldTransform,
    normalizeCertificateClassification,
} from '../../../../../Utils/certificateUtils'

const DEFAULT_LAYOUT = DEFAULT_CERTIFICATE_LAYOUT
const normalizeLayout = normalizeCertificateLayout
const getFieldTransform = getCertificateFieldTransform

const UserCertificates = ({ user }) => {
    const [certificates, setCertificates] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [viewCertModalOpen, setViewCertModalOpen] = useState(false)
    const [selectedCert, setSelectedCert] = useState(null)
    const [printAspectRatio, setPrintAspectRatio] = useState(1.414)

    useEffect(() => {
        if (user?.id) {
            const fetchUserCertificates = async () => {
                setIsLoading(true)
                try {
                    const data = await getStudentCertificates(user.id)
                    if (Array.isArray(data)) {
                        setCertificates(data)
                    } else {
                        setCertificates([])
                    }
                } catch (error) {
                    console.error('Error fetching certificates:', error)
                    message.error('Failed to fetch certificates')
                    setCertificates([])
                } finally {
                    setIsLoading(false)
                }
            }
            fetchUserCertificates()
        }
    }, [user?.id])

    const handleView = (record) => {
        if (record.TemplateID) {
            setSelectedCert(record)
            setViewCertModalOpen(true)
        } else if (record.CertificateURL) {
            window.open(record.CertificateURL, '_blank')
            message.success('Opening certificate...')
        } else {
            message.warning('Certificate not available for viewing')
        }
    }

    const getFieldTransform = (textAlign) => {
        if (textAlign === 'center') return 'translate(-50%, -50%)'
        if (textAlign === 'right') return 'translate(-100%, -50%)'
        return 'translate(0%, -50%)'
    }

    const columns = [
        {
            title: 'Certificate Number',
            dataIndex: 'CertificateNumber',
            key: 'CertificateNumber',
            render: (text) => text || 'N/A',
        },
        {
            title: 'Course',
            dataIndex: 'courseName',
            key: 'courseName',
            render: (text, record) => text || record.CourseName || 'N/A',
        },
        {
            title: 'Final Grade',
            dataIndex: 'Grade',
            key: 'Grade',
            render: (text) => text || 'Pass',
        },
        {
            title: 'Issue Date',
            dataIndex: 'IssueDate',
            key: 'IssueDate',
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : 'N/A'),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        size='small'
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                    >
                        View
                    </Button>
                    {record.CertificateURL && (
                        <a
                            href={record.CertificateURL}
                            download
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <Button
                                size='small'
                                icon={<DownloadOutlined />}
                            >
                                Download
                            </Button>
                        </a>
                    )}
                </Space>
            ),
        },
    ]

    const currentLayout = selectedCert?.LayoutJSON
        ? normalizeLayout(JSON.parse(selectedCert.LayoutJSON))
        : DEFAULT_LAYOUT

    return (
        <div style={{ padding: '20px' }}>
            <Spin spinning={isLoading} tip='Loading certificates...'>
                {certificates && certificates.length > 0 ? (
                    <Table
                        columns={columns}
                        dataSource={certificates.map((cert, index) => ({
                            ...cert,
                            key: cert.CertificateID || index,
                        }))}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 600 }}
                    />
                ) : (
                    <Empty
                        description='No certificates found'
                        style={{ marginTop: '50px' }}
                    />
                )}
            </Spin>

            <Modal
                title={null}
                open={viewCertModalOpen}
                onCancel={() => setViewCertModalOpen(false)}
                footer={(
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }} className="no-print">
                        <Button
                            type="primary"
                            icon={<PrinterOutlined />}
                            onClick={() => window.print()}
                        >
                            Print Certificate
                        </Button>
                        <Button onClick={() => setViewCertModalOpen(false)}>Close</Button>
                    </div>
                )}
                width={950}
                centered
                className="certificate-print-modal"
                wrapClassName="certificate-print-modal-wrap"
            >
                {viewCertModalOpen && selectedCert && (
                    <style>
                        {`
                          @media print {
                            @page {
                              size: ${printAspectRatio > 1 ? 'landscape' : 'portrait'};
                              margin: 0;
                            }
                          }
                        `}
                    </style>
                )}
                {selectedCert && selectedCert.BackgroundImageURL && (
                    <div className="printable-certificate-outer">
                        <div
                            className="printable-certificate-canvas"
                            style={{
                                position: 'relative',
                                width: '100%',
                                aspectRatio: printAspectRatio,
                                overflow: 'hidden',
                                background: '#ffffff',
                                border: '1px solid #d9d9d9',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            }}
                        >
                            <img
                                src={selectedCert.BackgroundImageURL}
                                alt="Bg Design"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                onLoad={(e) => {
                                    const { naturalWidth, naturalHeight } = e.target;
                                    if (naturalWidth && naturalHeight) {
                                        setPrintAspectRatio(naturalWidth / naturalHeight);
                                    }
                                }}
                            />
                            {currentLayout.studentName?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.studentName.top}%`,
                                        left: `${currentLayout.studentName.left}%`,
                                        transform: getFieldTransform(currentLayout.studentName.textAlign),
                                        fontSize: `${currentLayout.studentName.fontSize}px`,
                                        fontWeight: currentLayout.studentName.fontWeight,
                                        fontFamily: currentLayout.studentName.fontFamily || 'inherit',
                                        color: currentLayout.studentName.color,
                                        textAlign: currentLayout.studentName.textAlign,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        lineHeight: 1.25,
                                        maxWidth: `${currentLayout.studentName.maxWidth || 85}%`,
                                        width: 'max-content',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {formatCertificateText(selectedCert.studentName || user?.name)}
                                </div>
                            )}
                            {currentLayout.courseName?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.courseName.top}%`,
                                        left: `${currentLayout.courseName.left}%`,
                                        transform: getFieldTransform(currentLayout.courseName.textAlign),
                                        fontSize: `${currentLayout.courseName.fontSize}px`,
                                        fontWeight: currentLayout.courseName.fontWeight,
                                        fontFamily: currentLayout.courseName.fontFamily || 'inherit',
                                        color: currentLayout.courseName.color,
                                        textAlign: currentLayout.courseName.textAlign,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        lineHeight: 1.25,
                                        maxWidth: `${currentLayout.courseName.maxWidth || 80}%`,
                                        width: 'max-content',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {formatCertificateText(selectedCert.courseName || selectedCert.CourseName)}
                                </div>
                            )}
                            {currentLayout.certificateNumber?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.certificateNumber.top}%`,
                                        left: `${currentLayout.certificateNumber.left}%`,
                                        transform: getFieldTransform(currentLayout.certificateNumber.textAlign),
                                        fontSize: `${currentLayout.certificateNumber.fontSize}px`,
                                        fontWeight: currentLayout.certificateNumber.fontWeight,
                                        fontFamily: currentLayout.certificateNumber.fontFamily || 'inherit',
                                        color: currentLayout.certificateNumber.color,
                                        textAlign: currentLayout.certificateNumber.textAlign,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        lineHeight: 1.25,
                                        maxWidth: `${currentLayout.certificateNumber.maxWidth || 60}%`,
                                        width: 'max-content',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {formatCertificateText(selectedCert.CertificateNumber)}
                                </div>
                            )}
                            {currentLayout.issueDate?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.issueDate.top}%`,
                                        left: `${currentLayout.issueDate.left}%`,
                                        transform: getFieldTransform(currentLayout.issueDate.textAlign),
                                        fontSize: `${currentLayout.issueDate.fontSize}px`,
                                        fontWeight: currentLayout.issueDate.fontWeight,
                                        fontFamily: currentLayout.issueDate.fontFamily || 'inherit',
                                        color: currentLayout.issueDate.color,
                                        textAlign: currentLayout.issueDate.textAlign,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        lineHeight: 1.25,
                                        maxWidth: `${currentLayout.issueDate.maxWidth || 60}%`,
                                        width: 'max-content',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {selectedCert.IssueDate ? dayjs(selectedCert.IssueDate).format('YYYY-MM-DD') : ''}
                                </div>
                            )}
                            {currentLayout.finalGrade?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.finalGrade.top}%`,
                                        left: `${currentLayout.finalGrade.left}%`,
                                        transform: getFieldTransform(currentLayout.finalGrade.textAlign),
                                        fontSize: `${currentLayout.finalGrade.fontSize}px`,
                                        fontWeight: currentLayout.finalGrade.fontWeight,
                                        fontFamily: currentLayout.finalGrade.fontFamily || 'inherit',
                                        color: currentLayout.finalGrade.color,
                                        textAlign: currentLayout.finalGrade.textAlign,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        lineHeight: 1.25,
                                        maxWidth: `${currentLayout.finalGrade.maxWidth || 60}%`,
                                        width: 'max-content',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {(() => {
                                        const rawGrade = selectedCert.Grade || selectedCert.finalGrade || 'Pass';
                                        const gradeVal = normalizeCertificateClassification(rawGrade);
                                        const prefix = currentLayout.finalGrade.prefix !== undefined ? currentLayout.finalGrade.prefix : 'Grade: ';
                                        if (String(gradeVal).toLowerCase().startsWith('grade:')) {
                                            return gradeVal;
                                        }
                                        return `${prefix}${gradeVal}`;
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    )
}

export default UserCertificates
